'use client';

import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import type { EnterHomeCreationType } from '@enter/business-components/lib/pending-enter-home-prompt';
import { cn } from '@workspace/ui/lib/utils';
import {
    getNextTypewriterStep,
    INITIAL_TYPEWRITER_STATE,
    type TypewriterState,
} from '@/features/home/components/landing-v2/typewriter-placeholder';
import {
    createLandingJourneyId,
    getLandingButtonTrackingProps,
    useLandingV2Tracking,
    type LandingV2SubmitMethod,
} from '@/features/home/components/landing-v2/landing-v2-tracking';

const ChatBox = dynamic(() => import('@/features/home/components/chat-box').then((mod) => mod.default), {
    ssr: false,
    loading: () => <div className="h-[132px] w-full rounded-[26px] bg-background" aria-hidden="true" />,
});

interface LandingV2PromptProps {
    /** Submit handler owned by the existing Enter Home authentication flow. */
    onSubmit: (
        message: string,
        files: unknown[],
        creationType: EnterHomeCreationType,
        selectedModel: string,
        journeyId?: string,
    ) => Promise<void>;
    /** Currently selected model ID. */
    selectedModel: string;
    /** 当前首屏真实输入或移动 CTA 可交互时的旁路通知。 */
    onReady?: () => void;
    /** Optional custom class name. */
    className?: string;
}

function usePrefersReducedMotion() {
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);

        updatePreference();
        mediaQuery.addEventListener('change', updatePreference);
        return () => mediaQuery.removeEventListener('change', updatePreference);
    }, []);

    return prefersReducedMotion;
}

function useTypewriterPlaceholder(prompts: readonly string[], paused: boolean) {
    const prefersReducedMotion = usePrefersReducedMotion();
    const [typewriter, setTypewriter] = useState<TypewriterState>(INITIAL_TYPEWRITER_STATE);

    useEffect(() => {
        if (paused || prefersReducedMotion) return;

        const step = getNextTypewriterStep(typewriter, prompts);
        if (!step) return;

        const timeoutId = window.setTimeout(() => {
            setTypewriter(step.nextState);
        }, step.delay);

        return () => window.clearTimeout(timeoutId);
    }, [paused, prefersReducedMotion, prompts, typewriter]);

    if (prefersReducedMotion) return prompts[0] ?? '';
    return typewriter.displayedText;
}

export function LandingV2Prompt({ onSubmit, selectedModel, className, onReady }: LandingV2PromptProps) {
    const t = useTranslations('hero.chatBox');
    const { trackPromptSubmit } = useLandingV2Tracking();
    const creationType: EnterHomeCreationType = 'app';
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasInput, setHasInput] = useState(false);
    const prompts = useMemo(() => t.raw('appPrompts') as string[], [t]);
    const placeholder = useTypewriterPlaceholder(prompts, hasInput);

    useEffect(() => {
        if (window.matchMedia('(max-width: 768px)').matches) onReady?.();
    }, [onReady]);

    const handleTextChange = useCallback((text: string) => {
        setHasInput(text.trim().length > 0);
    }, []);

    const handleSubmit = useCallback(
        async (message: string, files: unknown[], meta?: { submitMethod?: LandingV2SubmitMethod }) => {
            if (isSubmitting) return;
            const trimmedMessage = message.trim();
            const journeyId = trimmedMessage ? createLandingJourneyId() : undefined;
            if (journeyId) {
                trackPromptSubmit({
                    creationType,
                    submitMethod: meta?.submitMethod ?? 'button',
                    promptLength: trimmedMessage.length,
                    journeyId,
                });
            }
            setIsSubmitting(true);
            try {
                await onSubmit(message, files, creationType, selectedModel, journeyId);
            } finally {
                setIsSubmitting(false);
            }
        },
        [creationType, isSubmitting, onSubmit, selectedModel, trackPromptSubmit],
    );

    const handleMobileStartBuilding = useCallback(() => {
        void handleSubmit('', []);
    }, [handleSubmit]);

    return (
        <div
            className={cn('mt-8 w-full max-w-[509px]', className)}
            id="open"
            aria-label={t('startBuilding')}
            data-testid="landing-v2-prompt"
        >
            <div className="landing-v2-prompt__desktop hidden min-[769px]:block">
                <ChatBox
                    onReady={selectedModel ? onReady : undefined}
                    isSubmitting={isSubmitting}
                    onSubmit={handleSubmit}
                    className="min-h-[132px] w-full rounded-[26px] border-0 bg-background p-2.5! text-foreground shadow-none"
                    composerRootClassName="border-transparent! bg-background! shadow-none! ring-0! focus-within:border-transparent! focus-within:ring-0!"
                    inputContainerClassName="h-[69px] min-h-[69px]! max-h-[69px]! overflow-y-auto! px-2.5 pt-2"
                    inputAreaClasses={{
                        root: 'h-[48px] overflow-y-auto',
                        editable: 'text-left [&_[data-slate-placeholder=true]]:!opacity-75',
                    }}
                    minRows={2}
                    maxRows={2}
                    placeholder={placeholder}
                    onTextChange={handleTextChange}
                    attachmentButtonClassName="landing-v2-prompt__attachment size-8! min-h-8! min-w-8! bg-transparent! p-0! text-foreground shadow-none!"
                    attachmentButtonWrapperProps={getLandingButtonTrackingProps({
                        buttonId: 'hero.prompt.attachment',
                        screenId: 'hero',
                        actionType: 'login',
                        creationType,
                        triggersLogin: true,
                    })}
                    submitButtonVariant="icon"
                    submitButtonClassName="landing-v2-prompt__submit"
                    submitButtonWrapperProps={getLandingButtonTrackingProps({
                        buttonId: 'hero.prompt.submit',
                        screenId: 'hero',
                        actionType: 'submit',
                        creationType,
                        triggersLogin: true,
                    })}
                />
            </div>
            <button
                type="button"
                className="landing-v2-prompt__mobile-cta mx-auto flex h-[58px] min-h-[58px] w-auto min-w-[262px] max-w-full items-center justify-center rounded-full bg-black px-[26px] font-normal leading-none text-white transition-transform duration-200 ease-out active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-wait disabled:opacity-60 min-[769px]:hidden motion-reduce:transition-none"
                onClick={handleMobileStartBuilding}
                disabled={isSubmitting}
                aria-busy={isSubmitting}
                {...getLandingButtonTrackingProps({
                    buttonId: 'hero.prompt.start_building',
                    screenId: 'hero',
                    actionType: 'login',
                    creationType,
                    triggersLogin: true,
                })}
            >
                {t('startBuilding')}
            </button>
        </div>
    );
}
